import React, { useState, useRef, useEffect } from 'react';
import { Plus } from 'lucide-react';
import type { Editor } from '@tiptap/react';

// ---- Merge variable definitions ----

interface MergeVarGroup {
  label: string;
  variables: { key: string; label: string }[];
}

const COMMON_VARS: MergeVarGroup = {
  label: 'Common',
  variables: [
    { key: 'member.firstName', label: 'First Name' },
    { key: 'member.lastName', label: 'Last Name' },
    { key: 'cu.name', label: 'Credit Union Name' },
    { key: 'cu.phone', label: 'Credit Union Phone' },
  ],
};

const LOAN_VARS: MergeVarGroup = {
  label: 'Loan',
  variables: [
    { key: 'loan.type', label: 'Loan Type' },
    { key: 'loan.amount', label: 'Loan Amount' },
    { key: 'loan.term', label: 'Loan Term' },
  ],
};

const ACCOUNT_VARS: MergeVarGroup = {
  label: 'Account',
  variables: [
    { key: 'account.type', label: 'Account Type' },
    { key: 'account.number_last4', label: 'Account Last 4' },
  ],
};

function getGroupsForEventType(eventType: string): MergeVarGroup[] {
  const groups: MergeVarGroup[] = [COMMON_VARS];
  if (eventType === 'new_loan') {
    groups.push(LOAN_VARS);
  }
  if (eventType === 'new_account' || eventType === 'account_closed') {
    groups.push(ACCOUNT_VARS);
  }
  return groups;
}

// ---- Props ----

interface MergeFieldPickerProps {
  editor: Editor | null;
  eventType: string;
}

// ---- Component ----

export function MergeFieldPicker({ editor, eventType }: MergeFieldPickerProps): React.ReactElement {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const groups = getGroupsForEventType(eventType);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;

    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current !== null && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  function handleInsert(variable: string): void {
    if (editor === null) return;
    editor
      .chain()
      .focus()
      .insertContent({ type: 'mergeVariable', attrs: { variable } })
      .run();
    setOpen(false);
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-line bg-white text-ink-soft hover:bg-bg transition-colors"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Plus className="h-3.5 w-3.5" aria-hidden />
        Insert Variable
      </button>

      {open && (
        <div
          className="absolute z-10 mt-1 bg-white border border-line rounded-lg shadow-lg p-2 w-64"
          role="listbox"
          aria-label="Merge variables"
        >
          {groups.map((group) => (
            <div key={group.label} className="mb-2 last:mb-0">
              <p className="px-2 py-1 text-xs font-semibold text-ink-muted uppercase tracking-wider">
                {group.label}
              </p>
              {group.variables.map((v) => (
                <button
                  key={v.key}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => handleInsert(v.key)}
                  className="w-full text-left px-2 py-1.5 text-sm text-ink-soft rounded hover:bg-info-soft hover:text-brand-dark transition-colors"
                >
                  <span className="font-mono text-xs bg-brand-tint text-brand-dark border border-info-soft rounded px-1 mr-2">
                    {`{{${v.key}}}`}
                  </span>
                  {v.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
